import { Link, Outlet, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Check } from "lucide-react";
import { api } from "../api/client";
import { useMe } from "../hooks/useMe";
import { THEMES, THEME_LABELS, useTheme } from "../theme";
import { Avatar } from "./Avatar";
import { NotificationBell } from "./NotificationBell";
import { OverflowMenu } from "./OverflowMenu";

export function Layout() {
  const { me } = useMe();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const qc = useQueryClient();

  async function logout() {
    await api.auth.logout();
    qc.setQueryData(["me"], null);
    navigate("/login");
  }

  const displayName = me?.displayName || me?.username || "";

  return (
    <div className="flex h-screen flex-col">
      <header className="flex items-center justify-between border-b border-slate-200 bg-[var(--surface)] px-4 py-3">
        <div className="flex items-center gap-4">
          <Link to="/boards" className="flex items-center gap-2 font-semibold">
            <span className="inline-block h-6 w-6 rounded bg-blue-600" />
            Kandoo
          </Link>
          <nav className="flex gap-3 text-sm text-slate-600">
            <Link to="/boards" className="hover:text-blue-600">
              Boards
            </Link>
            <Link to="/settings/orgs" className="hover:text-blue-600">
              Organizations
            </Link>
            <Link to="/settings/tokens" className="hover:text-blue-600">
              API tokens
            </Link>
            <Link to="/settings/account" className="hover:text-blue-600">
              Account
            </Link>
          </nav>
        </div>
        <div className="flex items-center gap-3 text-sm">
          {me && <NotificationBell />}
          {me && (
            <OverflowMenu
              ariaLabel="User menu"
              align="right"
              triggerClassName="flex items-center gap-2 rounded px-1.5 py-1 text-slate-700 hover:bg-slate-100"
              trigger={
                <>
                  <Avatar userId={me.id} name={displayName} />
                  <span className="max-w-[10rem] truncate">{displayName}</span>
                </>
              }
            >
              {(close) => (
                <div className="w-56">
                  <div className="flex items-center gap-2 px-3 py-2">
                    <Avatar userId={me.id} name={displayName} size="md" />
                    <div className="min-w-0">
                      <div className="truncate font-medium text-slate-700">
                        {displayName}
                      </div>
                      <div className="truncate text-xs text-slate-400">
                        @{me.username}
                      </div>
                    </div>
                  </div>

                  <div className="my-1 border-t border-slate-100" />
                  <div className="px-3 pb-0.5 pt-1 text-xs text-slate-400">
                    Theme
                  </div>
                  {THEMES.map((t) => (
                    <button
                      key={t}
                      type="button"
                      role="menuitemradio"
                      aria-checked={theme === t}
                      onClick={() => {
                        setTheme(t);
                        close();
                      }}
                      className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-slate-50"
                    >
                      <Check
                        size={14}
                        className={
                          theme === t ? "text-blue-600" : "text-transparent"
                        }
                      />
                      <span className="flex-1">{THEME_LABELS[t]}</span>
                    </button>
                  ))}

                  <div className="my-1 border-t border-slate-100" />
                  <Link
                    to="/settings/account"
                    role="menuitem"
                    onClick={close}
                    className="block px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
                  >
                    Account settings
                  </Link>

                  <div className="my-1 border-t border-slate-100" />
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      close();
                      logout();
                    }}
                    className="block w-full px-3 py-1.5 text-left text-sm text-slate-700 hover:bg-slate-50"
                  >
                    Log out
                  </button>
                </div>
              )}
            </OverflowMenu>
          )}
        </div>
      </header>
      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
}